import {
  BEHAVIOR_REVIEW_ALLOWED_ACTIONS,
  BEHAVIOR_REVIEW_DOMAIN,
  buildBehaviorReviewCandidate,
} from '../../utils/behaviorReviewCandidate';
import {
  buildGoalReviewCandidate,
  GOAL_REVIEW_ALLOWED_ACTIONS,
  GOAL_REVIEW_DOMAIN,
} from '../../utils/goalReviewCandidate';
import {
  buildProfileFactReviewCandidate,
  PROFILE_FACT_REVIEW_ALLOWED_ACTIONS,
  PROFILE_FACT_REVIEW_DOMAIN,
} from '../../utils/profileFactReviewCandidate';
import {
  buildCareSourceMedicationReviewCandidate,
  buildMedicationReviewCandidateFromResolution,
  MEDICATION_REVIEW_ALLOWED_ACTIONS,
  MEDICATION_REVIEW_DOMAIN,
} from '../../utils/medicationReviewCandidate';
import {
  buildTrackerEvidenceCandidate,
  TRACKER_EVIDENCE_ALLOWED_ACTIONS,
  TRACKER_EVIDENCE_REVIEW_DOMAIN,
} from '../../utils/trackerEvidenceCandidate';
import {
  ALLERGY_REVIEW_ALLOWED_ACTIONS,
  ALLERGY_REVIEW_DOMAIN,
  buildAllergyReviewCandidate,
} from '../../utils/allergyReviewCandidate';
import {
  buildDocumentDomainCandidatesFromFindings,
  DOCUMENT_REVIEW_ALLOWED_ACTIONS,
  DOCUMENT_REVIEW_DOMAIN,
} from '../../utils/documentReviewCandidate';
import {
  buildTimelineFactReviewCandidate,
  TIMELINE_FACT_REVIEW_ALLOWED_ACTIONS,
  TIMELINE_FACT_REVIEW_DOMAIN,
} from '../../utils/timelineFactReviewCandidate';

export const REVIEWABLE_ITEM_DOMAINS = Object.freeze({
  BEHAVIOR: BEHAVIOR_REVIEW_DOMAIN,
  GOAL: GOAL_REVIEW_DOMAIN,
  MEDICATION: MEDICATION_REVIEW_DOMAIN,
  PROFILE_FACT: PROFILE_FACT_REVIEW_DOMAIN,
  TRACKER_EVIDENCE: TRACKER_EVIDENCE_REVIEW_DOMAIN,
  ALLERGY: ALLERGY_REVIEW_DOMAIN,
  DOCUMENT: DOCUMENT_REVIEW_DOMAIN,
  TIMELINE_FACT: TIMELINE_FACT_REVIEW_DOMAIN,
});

export const REVIEWABLE_ITEM_KINDS = Object.freeze({
  MASTER_LIST: 'master_list',
  TRACKABLE_TARGET: 'trackable_target',
  EVIDENCE_LINK: 'evidence_link',
  PROFILE_FACT: 'profile_fact',
  TIMELINE_EVENT: 'timeline_event',
  DOCUMENT_SECTION: 'document_section',
});

export const allergyCandidateAdapter = Object.freeze({
  domain: ALLERGY_REVIEW_DOMAIN,
  kind: REVIEWABLE_ITEM_KINDS.MASTER_LIST,
  allowedActions: ALLERGY_REVIEW_ALLOWED_ACTIONS,
  buildCandidate: buildAllergyReviewCandidate,
  completeDecision: async ({
    candidate = {},
    decisionAction = '',
    context = {},
  } = {}) => {
    const { completeAllergyCandidateDecision } = await import('./allergyCandidateCompletion');
    return completeAllergyCandidateDecision({ candidate, decisionAction, context });
  },
});

export const medicationCandidateAdapter = Object.freeze({
  domain: MEDICATION_REVIEW_DOMAIN,
  kind: REVIEWABLE_ITEM_KINDS.MASTER_LIST,
  allowedActions: MEDICATION_REVIEW_ALLOWED_ACTIONS,
  buildCandidate: buildMedicationReviewCandidateFromResolution,
  buildCandidateFromResolution: buildMedicationReviewCandidateFromResolution,
  buildCandidateFromCareSource: buildCareSourceMedicationReviewCandidate,
  completeDecision: async ({
    candidate = {},
    decisionAction = '',
    target = null,
    context = {},
  } = {}) => {
    const { completeMedicationCandidateMasterListDecision } = await import('./medicationCandidateCompletion');
    return completeMedicationCandidateMasterListDecision({
      candidate,
      decisionAction,
      targetMedication: target,
      childId: context.childId || candidate.childId || candidate.origin?.childId || '',
      medication: context.medication || {},
      previousMedication: context.previousMedication || null,
      receiptAction: context.receiptAction,
      writeReceipt: context.writeReceipt,
      createTimelineEvent: Boolean(context.createTimelineEvent),
      audit: context.audit || {},
    });
  },
});

export const behaviorCandidateAdapter = Object.freeze({
  domain: BEHAVIOR_REVIEW_DOMAIN,
  kind: REVIEWABLE_ITEM_KINDS.TIMELINE_EVENT,
  allowedActions: BEHAVIOR_REVIEW_ALLOWED_ACTIONS,
  buildCandidate: buildBehaviorReviewCandidate,
  completeDecision: async ({
    candidate = {},
    decisionAction = '',
    context = {},
  } = {}) => {
    const { completeBehaviorCandidateTimelineDecision } = await import('./behaviorCandidateCompletion');
    return completeBehaviorCandidateTimelineDecision({
      candidate,
      decisionAction,
      context,
    });
  },
});

export const goalCandidateAdapter = Object.freeze({
  domain: GOAL_REVIEW_DOMAIN,
  kind: REVIEWABLE_ITEM_KINDS.TRACKABLE_TARGET,
  allowedActions: GOAL_REVIEW_ALLOWED_ACTIONS,
  buildCandidate: buildGoalReviewCandidate,
  completeDecision: async ({
    candidate = {},
    decisionAction = '',
    context = {},
  } = {}) => {
    const { completeGoalCandidateTrackableTargetDecision } = await import('./goalCandidateCompletion');
    return completeGoalCandidateTrackableTargetDecision({
      candidate,
      decisionAction,
      context,
    });
  },
});

export const profileFactCandidateAdapter = Object.freeze({
  domain: PROFILE_FACT_REVIEW_DOMAIN,
  kind: REVIEWABLE_ITEM_KINDS.PROFILE_FACT,
  allowedActions: PROFILE_FACT_REVIEW_ALLOWED_ACTIONS,
  buildCandidate: buildProfileFactReviewCandidate,
  completeDecision: async ({
    candidate = {},
    decisionAction = '',
    context = {},
  } = {}) => {
    const { completeProfileFactCandidateDecision } = await import('./profileFactCandidateCompletion');
    return completeProfileFactCandidateDecision({
      candidate,
      decisionAction,
      context,
    });
  },
});

export const trackerEvidenceCandidateAdapter = Object.freeze({
  domain: TRACKER_EVIDENCE_REVIEW_DOMAIN,
  kind: REVIEWABLE_ITEM_KINDS.EVIDENCE_LINK,
  allowedActions: TRACKER_EVIDENCE_ALLOWED_ACTIONS,
  buildCandidate: buildTrackerEvidenceCandidate,
  completeDecision: async ({
    candidate = {},
    decisionAction = '',
    context = {},
  } = {}) => {
    const { completeTrackerEvidenceDecision } = await import('./trackerEvidenceCompletion');
    return completeTrackerEvidenceDecision({
      candidate,
      decisionAction,
      context,
    });
  },
});

export const documentCandidateAdapter = Object.freeze({
  domain: DOCUMENT_REVIEW_DOMAIN,
  kind: REVIEWABLE_ITEM_KINDS.DOCUMENT_SECTION,
  allowedActions: DOCUMENT_REVIEW_ALLOWED_ACTIONS,
  buildCandidate: buildDocumentDomainCandidatesFromFindings,
  // NOTE: knowledge_fact and timeline_fact candidates resolve to their real domain
  // ('allergy', 'timeline_fact') at build time in documentReviewCandidate.js, so
  // they never reach this adapter — they dispatch straight to that domain's own
  // completeDecision. Only embedded_only stays on domain 'document', and is a no-op.
  completeDecision: async ({ candidate = {} } = {}) => {
    const extractedType = candidate.payload?.extractedType;
    if (extractedType === 'embedded_only') {
      return { ok: true, action: 'no_action_needed' };
    }
    throw new Error(
      `Document candidate completion for extractedType "${extractedType}" is not yet wired `
      + 'to an existing completion handler.'
    );
  },
});

export const timelineFactCandidateAdapter = Object.freeze({
  domain: TIMELINE_FACT_REVIEW_DOMAIN,
  kind: REVIEWABLE_ITEM_KINDS.TIMELINE_EVENT,
  allowedActions: TIMELINE_FACT_REVIEW_ALLOWED_ACTIONS,
  buildCandidate: buildTimelineFactReviewCandidate,
  completeDecision: async ({
    candidate = {},
    decisionAction = '',
    context = {},
  } = {}) => {
    const { completeTimelineFactCandidateDecision } = await import('./timelineFactCandidateCompletion');
    return completeTimelineFactCandidateDecision({ candidate, decisionAction, context });
  },
});

export const reviewableItemAdapters = Object.freeze({
  [ALLERGY_REVIEW_DOMAIN]: allergyCandidateAdapter,
  [BEHAVIOR_REVIEW_DOMAIN]: behaviorCandidateAdapter,
  [GOAL_REVIEW_DOMAIN]: goalCandidateAdapter,
  [MEDICATION_REVIEW_DOMAIN]: medicationCandidateAdapter,
  [PROFILE_FACT_REVIEW_DOMAIN]: profileFactCandidateAdapter,
  [TRACKER_EVIDENCE_REVIEW_DOMAIN]: trackerEvidenceCandidateAdapter,
  [DOCUMENT_REVIEW_DOMAIN]: documentCandidateAdapter,
  [TIMELINE_FACT_REVIEW_DOMAIN]: timelineFactCandidateAdapter,
});

export const getReviewableItemAdapter = (domain = '') => {
  const adapter = reviewableItemAdapters[String(domain || '').trim()];
  if (!adapter) {
    throw new Error(`No reviewable item adapter registered for "${domain}".`);
  }
  return adapter;
};

export const requireReviewableItemAdapter = getReviewableItemAdapter;
